<template>
  <div class="ui modal" id="productCostMarginModal" ref="costMarginModal">
    <i class="close icon" @click="closeModal"></i>
    <div class="header">
      Product Cost & Margin
    </div>
    <div class="content">
      <div class="subtitle">Configure costs to calculate accurate floor price</div>
      
      <div v-if="loading" class="loading-container">
        <div class="ui active centered inline loader"></div>
        <p>Loading product data...</p>
      </div>
      
      <div v-else-if="error" class="error-container">
        <i class="exclamation triangle icon"></i>
        <p>{{ error }}</p>
        <button class="ui button" @click="loadProductData">Retry</button>
      </div>
      
      <div v-else-if="productData">
        <div class="product-info-section">
          <div class="product-image">
            <img v-if="productData.product_info.image_url" :src="productData.product_info.image_url" :alt="productData.product_info.name">
            <div v-else class="product-placeholder">
              <i class="box icon"></i>
              <span>Product</span>
            </div>
          </div>
          <div class="product-details">
            <h3 class="product-name">{{ productData.product_info.name }}</h3>
            <div class="product-meta">
              <span class="asin">ASIN: {{ productData.product_info.asin }}</span>
            </div>
            <div class="current-price">
              Current: {{ currencySymbol }}{{ formatPrice(productData.product_info.current_price) }}
            </div>
          </div>
        </div>
        
        <!-- YOUR COSTS Section -->
        <div class="section">
          <h4 class="section-title">YOUR COSTS</h4>
          <div class="form-group">
            <label>Supplier Cost (COGS)</label>
            <div class="input-wrapper">
              <span class="currency">{{ currencySymbol }}</span>
              <input 
                type="number" 
                step="0.01" 
                min="0" 
                :value="formData.supplierCost"
                @input="handleSupplierCostInput"
                placeholder="0.00">
              <span class="hint">What you paid your supplier</span>
            </div>
          </div>
          <div class="form-group">
            <label>Other Costs (per unit)</label>
            <div class="input-wrapper">
              <span class="currency">{{ currencySymbol }}</span>
              <input 
                type="number" 
                step="0.01" 
                min="0" 
                :value="formData.otherCosts"
                @input="handleOtherCostsInput"
                placeholder="0.00">
              <span class="hint">Shipping, prep, labeling, customs — whatever applies to you</span>
            </div>
          </div>
          <div class="form-group total-cost">
            <label>Total Landed Cost</label>
            <div class="total-value">
              {{ currencySymbol }}{{ formatPrice(calculatedData.totalLandedCost) }}
            </div>
          </div>
        </div>
        
        <!-- Amazon Fees Section -->
        <div class="section">
          <h4 class="section-title">Amazon Fees (at {{ currencySymbol }}{{ formatPrice(productData.product_info.current_price) }})</h4>
          <div class="fee-row">
            <span>
              Referral Fee
              <span v-if="productData.amazon_fees.referral_fee_estimated" class="hint"
                    title="Amazon hasn't quoted this product's referral fee yet; using the most common rate (15%).">(estimated)</span>
            </span>
            <span class="fee-value">{{ currencySymbol }}{{ formatPrice(productData.amazon_fees.referral_fee) }}</span>
          </div>
          <div v-if="productData.amazon_fees.fba_fee_applies" class="fee-row">
            <span>FBA Fulfillment Fee</span>
            <span class="fee-value">{{ currencySymbol }}{{ formatPrice(productData.amazon_fees.fba_fulfillment_fee) }}</span>
          </div>
          <div class="fee-row total-fee">
            <span>Total Amazon Fees</span>
            <span class="fee-value">{{ currencySymbol }}{{ formatPrice(productData.amazon_fees.total) }}</span>
          </div>
        </div>
        
        <!-- TARGET MARGIN Section -->
        <div class="section">
          <h4 class="section-title">TARGET MARGIN</h4>
          <div class="radio-group">
            <div class="radio-option" :class="{ active: formData.useGlobalDefault }">
              <input 
                type="radio" 
                id="useGlobalDefault" 
                value="global" 
                v-model="marginMode"
                @change="onMarginModeChange">
              <label for="useGlobalDefault">
                <span class="radio-label">Use Global Default</span>
                <span class="radio-value">{{ productData.target_margin.global_default_percentage }}%</span>
                <span class="radio-hint">Same as all products</span>
              </label>
            </div>
            <div class="radio-option" :class="{ active: !formData.useGlobalDefault }">
              <input 
                type="radio" 
                id="customOverride" 
                value="custom" 
                v-model="marginMode"
                @change="onMarginModeChange">
              <label for="customOverride">
                <span class="radio-label">Custom Override</span>
                <span class="radio-hint">Set unique target</span>
              </label>
            </div>
          </div>
          <div v-if="!formData.useGlobalDefault" class="form-group custom-margin">
            <label>Custom Margin Target</label>
            <div class="input-wrapper">
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                max="100"
                :value="formData.customMarginPercentage"
                @input="handleCustomMarginInput"
                placeholder="0">
              <span class="percent">%</span>
            </div>
          </div>
        </div>
        
        <!-- Summary Section -->
        <div class="summary-section">
          <div class="summary-row">
            <span>Your Landed Cost</span>
            <span>{{ currencySymbol }}{{ formatPrice(calculatedData.totalLandedCost) }}</span>
          </div>
          <div class="summary-row">
            <span>Break-even Price</span>
            <span>{{ currencySymbol }}{{ formatPrice(calculatedData.breakEvenPrice) }}</span>
          </div>
          <div class="summary-row">
            <span>Current Margin @ {{ currencySymbol }}{{ formatPrice(productData.product_info.current_price) }}</span>
            <span :class="getMarginClass(calculatedData.currentMargin)">
              • {{ formatPrice(calculatedData.currentMargin.percentage) }}% ({{ currencySymbol }}{{ formatPrice(calculatedData.currentMargin.amount) }})
            </span>
          </div>
          <div class="summary-row floor-price">
            <span>Your Floor Price (@ {{ effectiveMarginPercentage }}% margin)</span>
            <span class="floor-price-value">
              {{ currencySymbol }}{{ formatPrice(calculatedData.floorPrice) }}
              <span v-if="!formData.useGlobalDefault" class="override-badge">OVERRIDE</span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="actions">
      <div class="footer-info">
        <i class="info circle icon"></i>
        <span>Fees auto-update when price changes</span>
      </div>
      <div class="footer-buttons">
        <button class="ui cancel button" @click="closeModal">Cancel</button>
        <button 
          class="ui primary button save-button" 
          @click="saveSettings"
          :class="{ loading: saving }">
          Save Settings
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'

export default {
  name: 'ProductCostMarginModal',
  data: function () {
    return {
      productId: null,
      productData: null,
      loading: false,
      error: null,
      saving: false,
      formData: {
        supplierCost: 0,
        otherCosts: 0,
        useGlobalDefault: true,
        customMarginPercentage: null
      },
      calculatedData: {
        totalLandedCost: 0,
        breakEvenPrice: 0,
        currentMargin: {
          percentage: 0,
          amount: 0
        },
        floorPrice: 0
      },
      marginMode: 'global'
    }
  },
  computed: {
    /** Price, fees and COGS are all in the listing marketplace's currency (EUR for .it, etc.). */
    currencySymbol: function () {
      return (this.productData && this.productData.currency_symbol) || ''
    },
    effectiveMarginPercentage: function () {
      if (this.formData.useGlobalDefault && this.productData) {
        return this.productData.target_margin.global_default_percentage
      }
      return this.formData.customMarginPercentage || 0
    }
  },
  methods: {
    open: function (productId) {
      if (!productId) {
        console.warn('ProductCostMarginModal: open() called without productId')
        return
      }
      
      this.productId = productId
      this.loading = true
      this.error = null
      this.productData = null
      
      this.$nextTick(() => {
        const $modal = $(this.$refs.costMarginModal)
        if (!$modal || !$modal.length) {
          console.error('ProductCostMarginModal: Modal element not found')
          return
        }
        
        // Destroy existing modal instance if any
        if ($modal.data('modal')) {
          $modal.modal('destroy')
        }
        
        // Initialize modal but don't show it yet
        $modal.modal({
          closable: true,
          autofocus: false,
          centered: true,
          onHidden: () => {
            this.resetForm()
          },
          onApprove: () => {
            return false // Prevent auto-close
          }
        })
        
        // Explicitly show the modal
        $modal.modal('show')
        this.loadProductData()
      })
    },
    closeModal: function () {
      const $modal = $(this.$refs.costMarginModal)
      if ($modal && $modal.length) {
        $modal.modal('hide')
        // If a blocking dialog ran while the modal was open, Semantic can leave body dimmed — clear when this modal hides
        this.$nextTick(function () {
          try {
            if (!$('.ui.modal.visible').length) {
              $('body').removeClass('dimmed')
            }
          } catch (e) {}
        })
      }
    },
    loadProductData: function () {
      if (!this.productId) {
        this.error = 'Product ID is missing'
        this.loading = false
        return
      }
      
      // Extract numeric ID from productId (remove PROD- prefix if present)
      const cleanId = String(this.productId).replace(/^PROD-/i, '')
      
      this.loading = true
      this.error = null
      
      this.$http.get(`/api/v1/products/${cleanId}/cost_margin`).then((response) => {
        this.loading = false
        if (response.body) {
          this.productData = response.body
          this.initializeForm()
          // Don't recalculate - use API response values
          // Only recalculate when user changes inputs
        } else {
          this.error = 'Failed to load product data'
        }
      }, (error) => {
        this.loading = false
        console.error('Error loading product cost/margin data:', error)
        if (error.body && error.body.error) {
          this.error = error.body.error
        } else if (error.body && error.body.message) {
          this.error = error.body.message
        } else {
          this.error = 'Failed to load product data. Please try again.'
        }
      })
    },
    initializeForm: function () {
      if (!this.productData) return
      
      // Initialize form with existing data from API response
      // Parse string values to numbers (API returns strings like "0.0")
      var supplierCost = this.productData.your_costs && this.productData.your_costs.supplier_cost
      var otherCosts = this.productData.your_costs && this.productData.your_costs.other_costs
      
      this.formData.supplierCost = supplierCost !== null && supplierCost !== undefined ? parseFloat(supplierCost) : 0
      this.formData.otherCosts = otherCosts !== null && otherCosts !== undefined ? parseFloat(otherCosts) : 0
      
      // Use API response for margin settings
      if (this.productData.target_margin) {
        this.formData.useGlobalDefault = this.productData.target_margin.use_global_default !== undefined 
          ? this.productData.target_margin.use_global_default 
          : true
        this.formData.customMarginPercentage = this.productData.target_margin.custom_override_percentage !== null && 
          this.productData.target_margin.custom_override_percentage !== undefined
          ? parseFloat(this.productData.target_margin.custom_override_percentage)
          : null
      } else {
        this.formData.useGlobalDefault = true
        this.formData.customMarginPercentage = null
      }
      
      // Set margin mode
      this.marginMode = this.formData.useGlobalDefault ? 'global' : 'custom'
      
      // Initialize calculated data from API response (use API values, don't recalculate)
      if (this.productData.calculations) {
        var calc = this.productData.calculations
        var currentMargin = calc.current_margin || {}
        
        this.calculatedData = {
          totalLandedCost: calc.total_landed_cost !== null && calc.total_landed_cost !== undefined 
            ? parseFloat(calc.total_landed_cost) 
            : 0,
          breakEvenPrice: calc.break_even_price !== null && calc.break_even_price !== undefined 
            ? parseFloat(calc.break_even_price) 
            : 0,
          currentMargin: {
            percentage: currentMargin.percentage !== null && currentMargin.percentage !== undefined 
              ? parseFloat(currentMargin.percentage) 
              : 0,
            amount: currentMargin.amount !== null && currentMargin.amount !== undefined 
              ? parseFloat(currentMargin.amount) 
              : 0
          },
          floorPrice: calc.floor_price !== null && calc.floor_price !== undefined 
            ? parseFloat(calc.floor_price) 
            : 0
        }
      } else {
        // Fallback to defaults if calculations not available
        this.calculatedData = {
          totalLandedCost: this.formData.supplierCost + this.formData.otherCosts,
          breakEvenPrice: 0,
          currentMargin: {
            percentage: 0,
            amount: 0
          },
          floorPrice: 0
        }
      }
    },
    handleSupplierCostInput: function (event) {
      var value = event.target.value
      if (value === '' || value === null || value === undefined) {
        this.formData.supplierCost = 0
      } else {
        // Parse as float and ensure it's a number
        var numValue = parseFloat(value)
        if (isNaN(numValue)) {
          this.formData.supplierCost = 0
        } else {
          if (numValue < 0) numValue = 0
          // Ensure it's stored as a number, not a string
          this.formData.supplierCost = Number(numValue.toFixed(2))
        }
      }
      this.calculateTotals()
    },
    handleOtherCostsInput: function (event) {
      var value = event.target.value
      if (value === '' || value === null || value === undefined) {
        this.formData.otherCosts = 0
      } else {
        var numValue = parseFloat(value)
        if (isNaN(numValue)) {
          this.formData.otherCosts = 0
        } else {
          if (numValue < 0) numValue = 0
          // Ensure it's stored as a number, not a string
          this.formData.otherCosts = Number(numValue.toFixed(2))
        }
      }
      this.calculateTotals()
    },
    handleCustomMarginInput: function (event) {
      var value = event.target.value
      if (value === '' || value === null || value === undefined) {
        this.formData.customMarginPercentage = null
      } else {
        var numValue = parseFloat(value)
        if (isNaN(numValue)) {
          this.formData.customMarginPercentage = null
        } else {
          if (numValue < 0) numValue = 0
          if (numValue > 100) numValue = 100
          this.formData.customMarginPercentage = numValue
        }
      }
      this.calculateTotals()
    },
    onMarginModeChange: function () {
      this.formData.useGlobalDefault = this.marginMode === 'global'
      if (this.formData.useGlobalDefault) {
        this.formData.customMarginPercentage = null
      }
      this.calculateTotals()
    },
    calculateTotals: function () {
      // Calculate total landed cost
      // Ensure values are numbers, not strings
      var supplierCost = typeof this.formData.supplierCost === 'number' 
        ? this.formData.supplierCost 
        : parseFloat(this.formData.supplierCost) || 0
      var otherCosts = typeof this.formData.otherCosts === 'number'
        ? this.formData.otherCosts
        : parseFloat(this.formData.otherCosts) || 0
      
      // Ensure we're adding numbers, not concatenating strings
      supplierCost = Number(supplierCost)
      otherCosts = Number(otherCosts)
      
      this.calculatedData.totalLandedCost = supplierCost + otherCosts
      
      if (this.productData && this.productData.amazon_fees) {
        const totalFees = this.productData.amazon_fees.total || 0
        this.calculatedData.breakEvenPrice = this.calculatedData.totalLandedCost + totalFees
      } else {
        this.calculatedData.breakEvenPrice = this.calculatedData.totalLandedCost
      }
      
      // Calculate current margin
      if (this.productData && this.productData.product_info && this.productData.product_info.current_price) {
        const price = this.productData.product_info.current_price
        const totalCost = this.calculatedData.totalLandedCost
        const totalFees = (this.productData.amazon_fees && this.productData.amazon_fees.total) || 0
        const profit = price - totalCost - totalFees
        this.calculatedData.currentMargin.amount = profit
        this.calculatedData.currentMargin.percentage = price > 0 ? (profit / price) * 100 : 0
      }
      
      // Floor price, same formula as the API's ProductCostMarginService#calculate_floor_price.
      // The referral fee scales with price (referral_fee_rate, from Amazon's quote for this
      // product); the FBA fee is flat: (landed + FBA fee) / (1 - margin% - referral rate)
      const marginDecimal = this.effectiveMarginPercentage / 100
      const amazonFees = (this.productData && this.productData.amazon_fees) ? this.productData.amazon_fees : {}
      const referralFeeRate = Number(amazonFees.referral_fee_rate)
      const denominator = 1 - marginDecimal - referralFeeRate

      if (this.calculatedData.totalLandedCost === 0 || isNaN(referralFeeRate)) {
        this.calculatedData.floorPrice = 0
      } else if (denominator > 0) {
        this.calculatedData.floorPrice = (this.calculatedData.totalLandedCost + (amazonFees.fba_fulfillment_fee || 0)) / denominator
      } else {
        this.calculatedData.floorPrice = 0
      }
    },
    saveSettings: function () {
      if (!this.productId) {
        alert('Error: Product ID is missing')
        return
      }
      
      // Validate inputs
      if (this.formData.supplierCost < 0) {
        alert('Supplier cost must be greater than or equal to 0')
        return
      }
      
      if (this.formData.otherCosts < 0) {
        alert('Other costs must be greater than or equal to 0')
        return
      }
      
      if (!this.formData.useGlobalDefault) {
        if (!this.formData.customMarginPercentage || this.formData.customMarginPercentage < 0 || this.formData.customMarginPercentage > 100) {
          alert('Please enter a valid custom margin percentage (0-100)')
          return
        }
      }
      
      this.saving = true
      const cleanId = String(this.productId).replace(/^PROD-/i, '')
      
      const payload = {
        cogs: this.formData.supplierCost,
        other_costs: this.formData.otherCosts
      }
      
      // Only include custom_margin_percentage if not using global default
      if (!this.formData.useGlobalDefault) {
        payload.custom_margin_percentage = this.formData.customMarginPercentage
      } else {
        payload.custom_margin_percentage = null
      }
      
      this.$http.put(`/api/v1/products/${cleanId}/update_cost_margin`, payload).then((response) => {
        this.saving = false
        const body = response.body || {}
        // Treat HTTP 200 as success unless body explicitly says success === false or includes top-level error
        const explicitFail = body.success === false || !!(body.error && String(body.error).trim())
        if (!explicitFail) {
          this.$emit('cost-margin-updated', this.productId)
          // Close before any UI that could steal focus (blocking alert left the dimmer/modal stuck for some users)
          this.closeModal()
        } else {
          window.alert(body.message || body.error || 'Failed to save settings. Please try again.')
        }
      }, (error) => {
        this.saving = false
        console.error('Error saving cost/margin settings:', error)
        if (error.body && error.body.errors && Array.isArray(error.body.errors)) {
          alert('Error: ' + error.body.errors.join(', '))
        } else if (error.body && error.body.error) {
          alert('Error: ' + error.body.error)
        } else {
          alert('Failed to save settings. Please try again.')
        }
      })
    },
    resetForm: function () {
      this.productId = null
      this.productData = null
      this.loading = false
      this.error = null
      this.saving = false
      this.formData = {
        supplierCost: 0,
        otherCosts: 0,
        useGlobalDefault: true,
        customMarginPercentage: null
      }
      this.calculatedData = {
        totalLandedCost: 0,
        breakEvenPrice: 0,
        currentMargin: {
          percentage: 0,
          amount: 0
        },
        floorPrice: 0
      }
      this.marginMode = 'global'
    },
    formatPrice: function (price) {
      if (price === null || price === undefined || isNaN(price)) return '0.00'
      return parseFloat(price).toFixed(2)
    },
    getMarginClass: function (margin) {
      if (margin.percentage >= 0) {
        return 'margin-positive'
      }
      return 'margin-negative'
    }
  }
}
</script>

<style scoped>
/* Modal Styles */
.ui.modal {
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  display: none !important; /* Hidden by default */
  flex-direction: column;
  margin-top: 0 !important;
  margin-left: auto !important;
  margin-right: auto !important;
  top: 5vh !important;
  position: fixed !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
}

/* Show modal only when Semantic UI adds the 'active' class */
.ui.modal.active {
  display: flex !important;
}

.ui.modal .content {
  max-height: calc(90vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
}

.header {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

/* Loading and Error States */
.loading-container,
.error-container {
  text-align: center;
  padding: 40px 20px;
}

.error-container {
  color: #ef4444;
}

.error-container i {
  font-size: 48px;
  margin-bottom: 16px;
}

/* Product Information Section */
.product-info-section {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #f3f4f6;
  border-radius: 8px;
  margin-bottom: 24px;
  overflow: visible;
}

.product-image {
  width: 88px;
  height: 88px;
  min-width: 88px;
  min-height: 88px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.product-image img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.product-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
}

.product-placeholder i {
  font-size: 32px;
  margin-bottom: 4px;
}

.product-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.product-meta {
  font-size: 13px;
  color: #6b7280;
}

.asin {
  font-family: monospace;
}

.current-price {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-top: auto;
}

/* Section Styles */
.section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Form Group Styles */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency,
.percent {
  font-size: 16px;
  font-weight: 600;
  color: #6b7280;
}

.input-wrapper input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  min-width: 0;
}

.input-wrapper input:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
}

.hint {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}

.total-cost {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.total-value {
  font-size: 18px;
  font-weight: 700;
  color: #f97316;
  background: #fff7ed;
  padding: 12px 16px;
  border-radius: 6px;
  text-align: right;
}

/* Fee Section */
.fee-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #374151;
}

.fee-row.total-fee {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  font-weight: 600;
}

.fee-value {
  font-weight: 600;
  color: #111827;
}

/* Radio Group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.radio-option.active {
  border-color: #f97316;
  background: #fff7ed;
}

.radio-option input[type="radio"] {
  margin-right: 12px;
  margin-top: 2px;
  cursor: pointer;
}

.radio-option label {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  margin: 0;
}

.radio-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.radio-value {
  font-size: 16px;
  font-weight: 700;
  color: #f97316;
}

.radio-hint {
  font-size: 12px;
  color: #6b7280;
}

.custom-margin {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.custom-margin .input-wrapper input {
  max-width: 120px;
}

/* Summary Section */
.summary-section {
  background: #1e293b;
  color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  margin-top: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.floor-price {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  font-weight: 700;
  font-size: 16px;
}

.floor-price-value {
  color: #f97316;
  display: flex;
  align-items: center;
  gap: 8px;
}

.override-badge {
  background: #f97316;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.margin-positive {
  color: #10b981;
}

.margin-negative {
  color: #ef4444;
}

/* Footer */
.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
}

.footer-info i {
  color: #9ca3af;
}

.footer-buttons {
  display: flex;
  gap: 12px;
}

.cancel.button {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.save-button {
  background: #f97316;
  color: #ffffff;
}

.save-button:hover {
  background: #ea580c;
}

/* Responsive */
@media (max-width: 768px) {
  .product-info-section {
    flex-direction: column;
  }
  
  .product-image {
    width: 100%;
    height: 200px;
  }
  
  .actions {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .footer-info {
    justify-content: center;
  }
  
  .footer-buttons {
    width: 100%;
  }
  
  .footer-buttons .button {
    flex: 1;
  }
}
</style>




// WEBPACK FOOTER //
// ProductCostMarginModal.vue?42ca77cb